export const ONBOARDING_STORAGE_KEY = 'checkoutOnboardingData';

export interface DecodedOnboardingData {
  sex?: 'male' | 'female' | '';
  age?: number;
  heightValue?: number;
  heightUnit?: 'cm' | 'in';
  heightFt?: number;
  heightIn?: number;
  weightValue?: number;
  weightUnit?: 'kg' | 'lb';
  goalWeightValue?: number;
  dietMode?: 'slimming' | 'stabilization' | 'put_on_weight' | 'gain_muscle_mass' | '';
  dietType?: string | null;
  dietId?: string | number;
  activityLevel?: string;
  spontaneousActivityLevel?: string;
  muscleGroups?: string[];
  workoutLocation?: string[];
  motivations?: string[];
  nutritionPreferences?: string[];
  dietSource?: string;
  strengthTrainingExperience?: string;
  hashimotoConditions?: string[];
  hashimotoSymptoms?: string[];
  hashimotoGlp1Usage?: string;
  preferredInstructors?: number[];
  exclusions?: number;
  tshValue?: number;
  [key: string]: unknown;
}

interface MeasurementInput<Unit extends string> {
  value: number;
  unit: Unit;
}

type CustomAttribute =
  | { key: string; stringValue: string }
  | { key: string; intValue: number }
  | { key: string; floatValue: number }
  | { key: string; boolValue: boolean }
  | { key: string; listValue: string[] };

export interface SaveOnboardingSettingsVariables {
  sex?: 'male' | 'female';
  age?: number;
  height?: MeasurementInput<'cm' | 'in'>;
  weight?: MeasurementInput<'kg' | 'lb'>;
  goalWeight?: MeasurementInput<'kg' | 'lb'>;
  dietMode?: 'slimming' | 'stabilization' | 'put_on_weight' | 'gain_muscle_mass';
  dietType?: string;
  dietId?: number;
  activityLevel?: string;
  spontaneousActivityLevel?: string;
  muscleGroups?: string[];
  workoutLocation?: string[];
  motivations?: string[];
  nutritionPreferences?: string[];
  dietSource?: string;
  strengthTrainingExperience?: string;
  hashimotoConditions?: string[];
  hashimotoSymptoms?: string[];
  hashimotoGlp1Usage?: string;
  preferredInstructors?: number[];
  exclusions?: number;
  hashimotoTsh?: number;
  customAttributes?: CustomAttribute[];
}

const TYPED_ONBOARDING_KEYS = new Set<string>([
  'sex',
  'age',
  'heightValue',
  'heightUnit',
  'heightFt',
  'heightIn',
  'weightValue',
  'weightUnit',
  'goalWeightValue',
  'dietMode',
  'dietType',
  'dietId',
  'activityLevel',
  'spontaneousActivity',
  'spontaneousActivityLevel',
  'muscleGroups',
  'workoutLocation',
  'motivations',
  'nutritionPreferences',
  'dietSource',
  'strengthWorkoutsExperience',
  'strengthTrainingExperience',
  'hashimotoConditions',
  'hashimotoSymptoms',
  'hashimotoGlp1Usage',
  'preferredInstructors',
  'exclusions',
  'tshValue',
]);

const MAX_CUSTOM_ATTRIBUTE_KEY_LENGTH = 128;

const VALID_DIET_MODES = new Set([
  'slimming',
  'stabilization',
  'put_on_weight',
  'gain_muscle_mass',
]);

export const decodeOnboardingData = (encoded: string): DecodedOnboardingData | null => {
  try {
    const normalized = encoded.replace(/ /g, '+');
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const buildHeight = (data: DecodedOnboardingData): MeasurementInput<'cm' | 'in'> | undefined => {
  if (data.heightUnit === 'in') {
    const totalInches = (data.heightFt || 0) * 12 + (data.heightIn || 0);
    return totalInches > 0 ? { value: totalInches, unit: 'in' } : undefined;
  }
  return data.heightValue && data.heightValue > 0
    ? { value: data.heightValue, unit: 'cm' }
    : undefined;
};

const buildWeight = (
  value: number | undefined,
  unit: 'kg' | 'lb' | undefined
): MeasurementInput<'kg' | 'lb'> | undefined => {
  if (!value || value <= 0) return undefined;
  return { value, unit: unit === 'lb' ? 'lb' : 'kg' };
};

const toInt = (value: string | number | undefined): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toCustomAttribute = (key: string, value: unknown): CustomAttribute | null => {
  if (Array.isArray(value)) {
    const strings = value.filter((item): item is string => typeof item === 'string' && item !== '');
    return strings.length ? { key, listValue: strings } : null;
  }
  if (typeof value === 'boolean') return { key, boolValue: value };
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return Number.isInteger(value) ? { key, intValue: value } : { key, floatValue: value };
  }
  if (typeof value === 'string') {
    return value ? { key, stringValue: value } : null;
  }
  return null;
};

const buildCustomAttributes = (data: DecodedOnboardingData): CustomAttribute[] => {
  const attributes: CustomAttribute[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (TYPED_ONBOARDING_KEYS.has(key)) continue;
    if (!key || key.length > MAX_CUSTOM_ATTRIBUTE_KEY_LENGTH) continue;
    const attribute = toCustomAttribute(key, value);
    if (attribute) attributes.push(attribute);
  }
  return attributes;
};

export const mapOnboardingToSaveVariables = (
  data: DecodedOnboardingData
): SaveOnboardingSettingsVariables => {
  const variables: SaveOnboardingSettingsVariables = {};

  if (data.sex === 'male' || data.sex === 'female') {
    variables.sex = data.sex;
  }
  if (data.age && data.age > 0) {
    variables.age = data.age;
  }
  if (data.dietMode && VALID_DIET_MODES.has(data.dietMode)) {
    variables.dietMode = data.dietMode as SaveOnboardingSettingsVariables['dietMode'];
  }

  const height = buildHeight(data);
  if (height) variables.height = height;

  const weight = buildWeight(data.weightValue, data.weightUnit);
  if (weight) variables.weight = weight;

  const goalWeight = buildWeight(data.goalWeightValue, data.weightUnit);
  if (goalWeight) variables.goalWeight = goalWeight;

  if (data.dietType) variables.dietType = data.dietType;

  const dietId = toInt(data.dietId);
  if (dietId !== undefined) variables.dietId = dietId;

  if (data.activityLevel) variables.activityLevel = data.activityLevel;
  if (data.spontaneousActivityLevel) {
    variables.spontaneousActivityLevel = data.spontaneousActivityLevel;
  }
  if (data.muscleGroups?.length) variables.muscleGroups = data.muscleGroups;
  if (data.workoutLocation?.length) variables.workoutLocation = data.workoutLocation;
  if (data.motivations?.length) variables.motivations = data.motivations;
  if (data.nutritionPreferences?.length) {
    variables.nutritionPreferences = data.nutritionPreferences;
  }
  if (data.dietSource) variables.dietSource = data.dietSource;
  if (data.strengthTrainingExperience) {
    variables.strengthTrainingExperience = data.strengthTrainingExperience;
  }
  if (data.hashimotoConditions?.length) variables.hashimotoConditions = data.hashimotoConditions;
  if (data.hashimotoSymptoms?.length) variables.hashimotoSymptoms = data.hashimotoSymptoms;
  if (data.hashimotoGlp1Usage) variables.hashimotoGlp1Usage = data.hashimotoGlp1Usage;
  if (data.preferredInstructors?.length) {
    variables.preferredInstructors = data.preferredInstructors;
  }
  if (data.exclusions && data.exclusions > 0) variables.exclusions = data.exclusions;
  if (data.tshValue && data.tshValue > 0) variables.hashimotoTsh = data.tshValue;

  const customAttributes = buildCustomAttributes(data);
  if (customAttributes.length) variables.customAttributes = customAttributes;

  return variables;
};
